import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js';

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    disableCloseFrom: {
      type: String,
      attribute: 'disable-close-from',
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    lockBody: {
      type: Boolean,
      attribute: 'lock-body',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    placement: {
      type: String,
      attribute: 'placement',
    },
  };

  $container = createRef();
  $details = createRef();

  constructor() {
    super();

    this.open = false;
    this.lockHeight = false;
    this.heightOffset = 3;
    this.breakpoint = '(min-width: 992px)';
    this.bodyListener = this.bodyListener.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.body.addEventListener('closeModal', this.close.bind(this));
  }

  disconnectedCallback() {
    enableBodyScroll(this.$container?.value);
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open && this.lockBody) {
        document.documentElement.classList.add('overflow-hidden');
      } else {
        document.documentElement.classList.remove('overflow-hidden');
      }

      if (this.open && this.lockHeight) {
        void this.scheduleSetHeight();
      }
    }
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen);
    }
    return super.requestUpdate(name, value);
  }

  toggle(e = false, value = undefined) {
    if (e && e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true;
    }
    if (e?.currentTarget?.classList.contains('is-open') && !this.open) return;
    e && e.preventDefault();
    this.open = typeof value === 'undefined' ? !this.open : value;
    this[`${this.open ? 'add' : 'remove'}BodyListener`]();
    this.open && this.broadcast();
    !this.open && this.broadcastClose();

    if (this.open) {
      // Ensure focus on the input
      setTimeout(() => {
        const input = document.getElementById('Search-In-Modal--desktop');
        if (input) {
          input.focus();
          input.classList.add('focus-visible');
        } else {
          console.warn('Input with ID "Search-In-Modal--desktop" not found.');
        }
      }, 0); // Delay to ensure DOM updates have completed
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle();
    }
  }

  close() {
    this.open && this.toggle();
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches;
  }

  addBodyListener() {
    this.isMobile() && !this.lockBody && disableBodyScroll(this.$container?.value);
    document.body.addEventListener('click', this.bodyListener);
  }

  removeBodyListener() {
    enableBodyScroll(this.$container?.value);
    document.body.removeEventListener('click', this.bodyListener);
  }

  bodyListener(event) {
    !(event.target.closest('.mini-cart') && this.disableCloseFrom === 'mini-cart')  && !this.open &&
      this.toggle();

  }

  /**
   * #content still has `hidden` during synchronous `toggle()`; measure after render so
   * getBoundingClientRect() is not all zeros (fixes wrong drawer height after scroll).
   */
  async scheduleSetHeight() {
    await this.updateComplete;
    if (!this.open || !this.lockHeight) return;
    requestAnimationFrame(() => {
      if (!this.open || !this.lockHeight) return;
      this.setHeight();
    });
  }

  setHeight() {
    const details = this.$details?.value;
    if (!this.isMobile() || !details) {
      if (this._height !== '') this._height = '';
      return;
    }

    const { top } = details.getBoundingClientRect();
    const insetTop = Math.max(0, top - this.heightOffset);
    const next = `calc(100dvh - ${Math.floor(insetTop)}px)`;
    if (this._height !== next) {
      this._height = next;
    }
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      }),
    );
  }

  broadcastClose() {
    this.dispatchEvent(
      new CustomEvent('modalClosed', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
          placement: this.placement,
        },
      }),
    );
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <div ${ref(this.$container)}>
        <hh-button
          @click="${this.toggle}"
          class="decoration-none"
          aria-controls="content"
          aria-expanded="${this.open}"
          .aria_label="${this.label}"
          aria-haspopup="dialog"
          theme='tertiary'
          ${ref(this.$container)}
        >
          <slot
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
          ></slot>
        </hh-button>

        <div
          id="content"
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ?hidden="${!this.open}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
            ></slot>
          </hh-focus-trap>
        </div>
      </div>`;
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }

    hh-button {
      display: flex;
      flex-direction: column;
    }
  `;
}

customElements.define('hh-details-modal', HHDetailsModal);
